// O que vira mostrador e o que vira barra no painel, por tag (a tag é a identidade da
// grandeza; o endereço muda entre modelos). As faixas são em pu do nominal do regulador
// (ver apps/api/src/nominal.ts): 1,0 = nominal.
//
// Dois tipos de mostrador:
//   - magnitude: arco preenchido do zero até o valor (tensão, corrente, potência ativa...);
//   - desvio: só ponteiro, com o nominal no alto do arco (frequência, potência reativa) —
//     preencher do mínimo até o valor diria "quanto" quando o que importa é "para que lado".

import type { NominalKey } from "./api";

export type GaugeMode = "magnitude" | "deviation";

export interface GaugeDef {
  tag: string;
  title: string;
  nominal: NominalKey | "frequency";
  /** Faixa em pu do nominal. */
  range: [number, number];
  mode: GaugeMode;
}

export const GAUGES: Record<string, GaugeDef> = {
  an_varm: { tag: "an_varm", title: "Tensão de armadura", nominal: "voltage", range: [0, 1.3], mode: "magnitude" },
  an_iarm: { tag: "an_iarm", title: "Corrente de armadura", nominal: "current", range: [0, 1.3], mode: "magnitude" },
  an_potp: { tag: "an_potp", title: "Potência ativa", nominal: "power", range: [0, 1.2], mode: "magnitude" },
  an_potq: { tag: "an_potq", title: "Potência reativa", nominal: "power", range: [-0.6, 0.6], mode: "deviation" },
  an_farm: { tag: "an_farm", title: "Frequência", nominal: "frequency", range: [0.95, 1.05], mode: "deviation" },
  an_vdc_pt: { tag: "an_vdc_pt", title: "Tensão de campo", nominal: "fieldVoltage", range: [0, 1.5], mode: "magnitude" },
  an_idc_pt: { tag: "an_idc_pt", title: "Corrente de campo", nominal: "fieldCurrent", range: [0, 1.5], mode: "magnitude" },
  an_vel: { tag: "an_vel", title: "Rotação", nominal: "speed", range: [0, 1.2], mode: "magnitude" },
};

/** Os quatro da Visão geral: o estado da máquina de relance. */
export const HOME_GAUGES = ["an_varm", "an_potp", "an_potq", "an_farm"];
/** Carregamento em % do nominal, na Visão geral. */
export const HOME_LOAD = [
  { tag: "an_iarm", label: "Corrente de armadura", nominal: "current" },
  { tag: "an_pots", label: "Potência aparente", nominal: "power" },
  { tag: "an_idc_pt", label: "Corrente de campo", nominal: "fieldCurrent" },
  { tag: "an_vdc_pt", label: "Tensão de campo", nominal: "fieldVoltage" },
] as const;

export const REGULATOR_GAUGES = ["an_varm", "an_iarm", "an_potp", "an_potq", "an_farm", "an_vdc_pt", "an_idc_pt", "an_vel"];

/** Grupos de barras do painel do regulador: mesma unidade e mesmo nominal em cada grupo. */
export const BAR_GROUPS: { title: string; nominal: NominalKey | null; tags: [string, string][] }[] = [
  {
    title: "Tensões de fase",
    nominal: "voltage",
    tags: [
      ["an_varm_ab", "AB"],
      ["an_varm_bc", "BC"],
      ["an_varm_ca", "CA"],
      ["an_vbus_ca", "Barramento CA"],
    ],
  },
  {
    title: "Correntes de fase",
    nominal: "current",
    tags: [
      ["an_iarm_a", "A"],
      ["an_iarm_b", "B"],
      ["an_iarm_c", "C"],
    ],
  },
  {
    title: "Pontes retificadoras",
    nominal: "fieldCurrent",
    tags: [
      ["an_idc_pt1", "Ponte 1 (CC)"],
      ["an_idc_pt2", "Ponte 2 (CC)"],
      ["an_iac_pt", "Soma CA"],
    ],
  },
  {
    // Já em % (base Q13_P): sem nominal, escala fixa de 0 a 100 %.
    title: "Posições e entradas",
    nominal: null,
    tags: [
      ["an_distr", "Distribuidor"],
      ["an_rotor", "Pás do rotor"],
      ["an_in_g1", "Entrada genérica 1"],
      ["an_in_g2", "Entrada genérica 2"],
    ],
  },
];

/** Tudo o que já aparece em mostrador ou barra (o resto vai para os cartões numéricos). */
export const CHARTED_TAGS = new Set([...REGULATOR_GAUGES, ...BAR_GROUPS.flatMap((g) => g.tags.map(([t]) => t))]);
